import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useBasket } from '@hook/useBasket';
import { useHeaderState } from '@hook/useHeaderState';
import { useDesktopOrWider } from '@utils/mediaQuery';
import { useIsClient } from '@utils/common';
import { CartModal } from '@components/CartModal';
import { LayoutContainer } from '@ui/LayoutContainer';
import { HeaderDesktop } from '@outlet/components/HeaderDesktop';
import { CatalogTree } from '@components/SecondaryHeaderDesktop';
import { HeaderCatalogMenuDesktop } from '@components/HeaderCatalogMenuDesktop';
import { HeaderMobile } from '@components/HeaderMobile';
import { HeaderCatalogMenuMobile } from '@components/HeaderCatalogMenuMobile';
import { MobileBottomBar } from '@outlet/components/MobileBottomBar';

import {
  useDesktopFastLinks,
  useUserMenuLinks,
  useMobileFastLinks,
} from './utils';

import s from './Header.sss';

export interface CatalogTreeStructure {
  structure: CatalogTree,
}

interface User {
  id: number,
  username: string,
  avatar?: string | null,
  unreadMessagesCount: number,
  hasUnreadNotifications?: boolean | null,
  isSeller: boolean,
}

interface Props {
  user?: User | null,
  productsCount?: number | null,
  catalogTreeJSON?: string | null,
  isUserLoading?: boolean,
  isCatalogTreeLoading?: boolean,
  isMobileBottomBarEnabled?: boolean,
}

export const Header: React.FC<Props> = ({
  user,
  productsCount,
  catalogTreeJSON,
  isUserLoading,
  isCatalogTreeLoading,
  isMobileBottomBarEnabled = true,
}) => {
  const destopCatalogMenuRef = useRef<HTMLDivElement>(null);
  const [isDesktopHeaderFixed, setIsDesktopHeaderFixed] = useState(false);

  const { ui: basketUI } = useBasket({ lazy: true });
  const isClient = useIsClient();
  const isDesktop = useDesktopOrWider();

  const {
    isHeaderCatalogMenuOpen,
    handleCloseHeaderCatalogMenu,
    handleToggleHeaderCatalogMenu,
  } = useHeaderState();

  const desktopFastLinks = useDesktopFastLinks({
    unreadMessagesCount: user?.unreadMessagesCount,
    productsCount,
  });

  const mobileFastLinks = useMobileFastLinks({
    productsCount,
  });

  const userMenuLinks = useUserMenuLinks();

  useEffect(() => {
    if (isHeaderCatalogMenuOpen && isDesktop && destopCatalogMenuRef.current) {
      const topElementPosition = destopCatalogMenuRef.current.getBoundingClientRect().top;
      const totalPageHeight = window.document.documentElement.clientHeight;

      destopCatalogMenuRef.current.style.height = `${totalPageHeight - topElementPosition}px`;
    }
  }, [isHeaderCatalogMenuOpen, isDesktop]);

  useEffect(() => {
    const onScroll = () => setIsDesktopHeaderFixed(window.scrollY > 100);
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 350 && isDesktop) {
        handleCloseHeaderCatalogMenu();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, [handleCloseHeaderCatalogMenu, isDesktop]);

  const catalogTree = catalogTreeJSON ? JSON.parse(catalogTreeJSON) as CatalogTreeStructure : null;

  return (
    <>
      <header
        className={s.root}
        onClick={() => {
          if (isDesktop) {
            handleCloseHeaderCatalogMenu();
          }
        }}
      >
        <div className={s.desktopHeaderHolder}>
          <div
            className={classNames(s.desktopHeader, {
              [s.desktopHeaderStateSticky]: isDesktopHeaderFixed,
            })}
          >
            <LayoutContainer>
              <HeaderDesktop
                user={user}
                catalogName={catalogTree?.structure.name}
                rootCatalogs={catalogTree?.structure.subCatalogs}
                fastLinks={desktopFastLinks}
                userMenuLinks={userMenuLinks}
                isUserLoading={isUserLoading}
                onMenuToggle={handleToggleHeaderCatalogMenu}
              />
            </LayoutContainer>

            <div
              ref={destopCatalogMenuRef}
              className={classNames(s.desktopCatalogMenu, {
                [s.desktopCatalogMenuStateOpen]: isHeaderCatalogMenuOpen && isDesktop,
              })}
            >
              <LayoutContainer className={s.desktopCatalogMenuContainer}>
                <HeaderCatalogMenuDesktop
                  catalogTree={catalogTree?.structure}
                  staticLinks={[]}
                  isCatalogTreeLoading={isCatalogTreeLoading}
                />
              </LayoutContainer>
            </div>
          </div>
        </div>

        <div className={s.mobileHeaderHolder}>
          <HeaderMobile
            className={s.mobileHeader}
            rootCatalogs={catalogTree?.structure.subCatalogs}
            fastLinks={mobileFastLinks}
            onOpenMenu={handleToggleHeaderCatalogMenu}
          />
        </div>

        <HeaderCatalogMenuMobile
          isOpen={isHeaderCatalogMenuOpen && !isDesktop}
          catalogTree={catalogTree?.structure}
          staticLinks={[]}
          isSalesLinkEnabled={false}
          isCatalogTreeLoading={isCatalogTreeLoading}
          onClose={handleCloseHeaderCatalogMenu}
        />
      </header>

      {isClient && isMobileBottomBarEnabled && (
        <MobileBottomBar
          className={s.mobileBottomBar}
          user={user}
          userMenuLinks={userMenuLinks}
        />
      )}

      <CartModal
        isOpen={basketUI.isOpen}
        onRequestClose={basketUI.close}
      />
    </>
  );
};
